import React, { useState, useEffect } from 'react';
import { Typography, FormControlLabel, Switch, Button } from '@material-ui/core';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import CheckIcon from '@material-ui/icons/Check';
import PhoneInTalkIcon from '@material-ui/icons/PhoneInTalk';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import { AvailabilityModal } from './AvailabilityModal';
import { ErrorAPI } from '@shared/components/ui/ErrorAPI';
//import { AlertUser } from '@shared/components/feedback/AlertUser';
import dayjs from 'dayjs';
import Auth from '@shared/services/Auth';
import { useInProgressActivity } from '@features/activities/hooks/useActivityQueries';
import { useQuery } from '@tanstack/react-query';
import { useAvailabilityBlockStyles } from '@shared/styles/AvailabilityBlock.styles';
import { useUpdateUserAvailability } from '@shared/hooks/queries/useUserQueries';
import api from '@shared/api/api';
import { getUserTimezone } from '@shared/utils/timezone';

// ------------------------------------------------------------------------ //
// COMPONENT
// ------------------------------------------------------------------------ //
const AvailabilityBlock = ({ setInChat, inChat, startUnavailability }) => {
  const classes = useAvailabilityBlockStyles();
  const [inCall, setInCall] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [endDate, setEndDate] = useState();
  const [errorAPI, setErrorAPI] = useState(false);
  const { user } = Auth.user();

  // Utilisation des hooks React Query
  const { data: inProgressActivity } = useInProgressActivity();
  const { mutate: updateAvailability, isLoading } = useUpdateUserAvailability();

  // Poll user info every 2 seconds
  const { data: userInfo, isError } = useQuery({
    queryKey: ['userInfo', user.id],
    queryFn: () => api.getUserInfo(user.id),
    refetchInterval: 2000, // Polling every 2 seconds
    staleTime: 0,
  });

  useEffect(() => {
    if (isError) {
      setErrorAPI(true);
    }
  }, [isError]);

  // Synchronize switches and modal with user availability status
  useEffect(() => {
    if (userInfo?.user) {
      // User is unavailable
      if (userInfo.user.available === 0) {
        // Update switches to reflect unavailability
        setInCall(true);
        // Open modal only if not already open
        if (!openModal) {
          setOpenModal(true);
        }
      }
      // User is available
      else if (userInfo.user.available === 1) {
        // Reset switches
        setInCall(false);
        setInChat(false);
        // Close modal if open
        if (openModal) {
          setOpenModal(false);
        }
      }
    }
  }, [userInfo]);

  const handleChangeCall = (event) => {
    if (event.target.checked === true) {
      const userTimezone = getUserTimezone();
      var start = dayjs().tz(userTimezone).format('YYYY-MM-DDTHH:mm');
      var end = dayjs().tz(userTimezone).add(20, 'minute').format('YYYY-MM-DDTHH:mm');
      handleUnavailable(start, end);
    }
    if (event.target.checked === false) {
      handleAvailable();
    }
    setInCall(event.target.checked);
  };

  const handleChangeChat = (event) => {
    if (event.target.checked === true) {
      const userTimezone = getUserTimezone();
      var start = dayjs().tz(userTimezone).format('YYYY-MM-DDTHH:mm');
      var end = dayjs().tz(userTimezone).add(20, 'minute').format('YYYY-MM-DDTHH:mm');

      handleUnavailable(start, end);
    }
    if (event.target.checked === false) {
      handleAvailable();
    }
    setInChat(event.target.checked);
  };

  const handleOpenModal = (event) => {
    setOpenModal(true);
  };

  const handleCloseModal = (event) => {
    setOpenModal(false);
  };

  const handleUnavailable = (start, end) => {
    updateAvailability(
      {
        userId: user.id,
        available: 0,
        unavailableStartedAt: start,
        unavailableEndedAt: end,
      },
      {
        onError: (error) => {
          console.log(error);
          setErrorAPI(true);
        },
      }
    );
  };

  const handleAvailable = () => {
    updateAvailability(
      {
        userId: user.id,
        available: 1,
        unavailableStartedAt: null,
        unavailableEndedAt: null,
      },
      {
        onError: (error) => {
          console.log(error);
          setErrorAPI(true);
        },
      }
    );
  };

  const available = !inCall && !inChat;
  
  return !inProgressActivity ? null : (
    <div className={classes.row}>

      {/* <AlertUser
        alertUser={alertAvailability}
        setAlertUser={() => {
          setAlertAvailability(false);
        }}
        message={"Veuillez l'arrêter ou la prolonger dans la fenêtre de détail d'indisponibilité"}
        title={'Votre indisponibilité se termine bientôt !'}
        type={'warning'}
        position={'left'}
      /> */}

      <div className={classes.chatCallColumn}>
        <ErrorAPI
          errorAPI={errorAPI}
          setErrorAPI={setErrorAPI}
          message={
            'Un problème est survenu lors du changement de disponibilité, veuillez recharger la page ou réessayer plus tard.'
          }
        />
        <div className={classes.chatCallRow}>
          <FormControlLabel
            control={<Switch checked={inCall} onChange={handleChangeCall} name="inCall" color="primary" />}
            label="Appel en cours"
          />
          <PhoneInTalkIcon color="primary" />
        </div>
        <div className={classes.chatCallRow}>
          <FormControlLabel
            control={<Switch checked={inChat} onChange={handleChangeChat} name="inChat" color="primary" />}
            label="Discussion en cours"
          />
          <QuestionAnswerIcon color="primary" />
        </div>
      </div>
      <div className={classes.availabilityColumn}>
        {!available ? (
          <div className={classes.availableTextRow}>
            <Typography variant="h6" color="error">
              {user.first_name} {user.last_name} : Indisponible jusqu'a {dayjs(endDate).format('HH[H]mm')}
            </Typography>
            <NotInterestedIcon color="error" />
          </div>
        ) : (
          <div className={classes.availableTextRow}>
            <Typography variant="h6" color="primary">
              {user.first_name} {user.last_name} : Disponible
            </Typography>
            <CheckIcon color="primary" />
          </div>
        )}

        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={available || isLoading}
          className={classes.button}
          onClick={handleOpenModal}
        >
          Détail indisponibilité
        </Button>

        <AvailabilityModal
          openModal={openModal}
          handleCloseModal={handleCloseModal}
          // setAlertAvailability={setAlertAvailability}
          setAlertAvailability={() => {}}
          userId={user.id}
          available={false}
          setAvailabilityEndDate={setEndDate}
        />
      </div>
    </div>
  );
};


export default AvailabilityBlock;